import type { CalcFn, DateValue, PropertyType } from './db-types';
import { locale, t } from '@lumen/platform';

/** Empty in Notion's sense: no value, empty text, empty list, or an unchecked checkbox. */
export function isEmptyValue(v: unknown): boolean {
  return v === null || v === undefined || v === '' || v === false || (Array.isArray(v) && v.length === 0);
}

const flat = (values: unknown[]) => values.flatMap((v) => (Array.isArray(v) ? v : [v])).filter((v) => !isEmptyValue(v));
const nums = (values: unknown[]) => flat(values).filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
const key = (v: unknown) => (typeof v === 'object' ? JSON.stringify(v) : String(v));

/** Milliseconds for a date-like value: DateValue, ISO string or timestamp. */
export function dateMs(v: unknown): number | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v);
    return Number.isNaN(t) ? null : t;
  }
  if (v && typeof v === 'object' && 'start' in v) {
    const d = v as DateValue;
    return dateMs(d.time ? `${d.start}T${d.time}` : d.start);
  }
  return null;
}

const dates = (values: unknown[]) => values.map(dateMs).filter((t): t is number => t !== null);

export type CalcResult = number | string | unknown[] | null;

/** Runs a column calculation (table footer) or rollup aggregation over cell values. */
export function computeCalc(fn: CalcFn, values: unknown[]): CalcResult {
  const total = values.length;
  const empty = values.filter(isEmptyValue).length;
  switch (fn) {
    case 'none':
      return null;
    case 'show_original':
      return flat(values);
    case 'show_unique': {
      const seen = new Map<string, unknown>();
      for (const v of flat(values)) if (!seen.has(key(v))) seen.set(key(v), v);
      return [...seen.values()];
    }
    case 'count_all':
      return total;
    case 'count_values':
      return flat(values).length;
    case 'count_unique':
      return new Set(flat(values).map(key)).size;
    case 'count_empty':
      return empty;
    case 'count_not_empty':
      return total - empty;
    case 'percent_empty':
      return total ? empty / total : 0;
    case 'percent_not_empty':
      return total ? (total - empty) / total : 0;
    case 'sum':
      return nums(values).reduce((a, b) => a + b, 0);
    case 'average': {
      const n = nums(values);
      return n.length ? n.reduce((a, b) => a + b, 0) / n.length : null;
    }
    case 'median': {
      const n = nums(values).sort((a, b) => a - b);
      if (!n.length) return null;
      const m = Math.floor(n.length / 2);
      return n.length % 2 ? n[m]! : (n[m - 1]! + n[m]!) / 2;
    }
    case 'min': {
      const n = nums(values);
      return n.length ? Math.min(...n) : null;
    }
    case 'max': {
      const n = nums(values);
      return n.length ? Math.max(...n) : null;
    }
    case 'range': {
      const n = nums(values);
      return n.length ? Math.max(...n) - Math.min(...n) : null;
    }
    case 'earliest_date': {
      const d = dates(values);
      return d.length ? Math.min(...d) : null;
    }
    case 'latest_date': {
      const d = dates(values);
      return d.length ? Math.max(...d) : null;
    }
    case 'date_range': {
      const d = dates(values);
      return d.length ? Math.round((Math.max(...d) - Math.min(...d)) / 86_400_000) : null;
    }
    case 'checked':
      return values.filter((v) => v === true).length;
    case 'unchecked':
      return values.filter((v) => v !== true).length;
    case 'percent_checked':
      return total ? values.filter((v) => v === true).length / total : 0;
    case 'percent_unchecked':
      return total ? values.filter((v) => v !== true).length / total : 0;
  }
}

export const CALC_LABELS: Record<CalcFn, string> = {
  none: t('None'),
  show_original: t('Show original'),
  show_unique: t('Show unique values'),
  count_all: t('Count all'),
  count_values: t('Count values'),
  count_unique: t('Count unique values'),
  count_empty: t('Count empty'),
  count_not_empty: t('Count not empty'),
  percent_empty: t('Percent empty'),
  percent_not_empty: t('Percent not empty'),
  sum: t('Sum'),
  average: t('Average'),
  median: t('Median'),
  min: t('Min'),
  max: t('Max'),
  range: t('Range'),
  earliest_date: t('Earliest date'),
  latest_date: t('Latest date'),
  date_range: t('Date range'),
  checked: t('Checked'),
  unchecked: t('Unchecked'),
  percent_checked: t('Percent checked'),
  percent_unchecked: t('Percent unchecked'),
};

const COUNTS: CalcFn[] = ['count_all', 'count_values', 'count_unique', 'count_empty', 'count_not_empty', 'percent_empty', 'percent_not_empty'];
const NUMERIC: CalcFn[] = ['sum', 'average', 'median', 'min', 'max', 'range'];
const DATES: CalcFn[] = ['earliest_date', 'latest_date', 'date_range'];
const CHECKS: CalcFn[] = ['checked', 'unchecked', 'percent_checked', 'percent_unchecked'];

/** Calculations offered in a table footer for a property type. */
export function calcsFor(type: PropertyType): CalcFn[] {
  if (type === 'checkbox') return ['none', 'count_all', ...CHECKS];
  if (type === 'number' || type === 'unique_id') return ['none', ...COUNTS, ...NUMERIC];
  if (type === 'date' || type === 'created_time' || type === 'last_edited_time') return ['none', ...COUNTS, ...DATES];
  if (type === 'formula' || type === 'rollup') return ['none', ...COUNTS, ...NUMERIC, ...DATES, ...CHECKS];
  return ['none', ...COUNTS];
}

/** Calculations offered for a rollup. */
export const ROLLUP_CALCS: CalcFn[] = ['show_original', 'show_unique', ...COUNTS, ...NUMERIC, ...DATES, ...CHECKS];

/** Footer display text: percentages as "%", dates as ISO days, day spans as "N days". */
export function formatCalc(fn: CalcFn, v: CalcResult): string {
  if (v === null) return '';
  if (Array.isArray(v)) return v.map(String).join(', ');
  if (fn.startsWith('percent')) return `${Math.round((v as number) * 1000) / 10}%`;
  if (fn === 'earliest_date' || fn === 'latest_date') return new Date(v as number).toLocaleDateString(locale() ?? 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (fn === 'date_range') return `${v} day${v === 1 ? '' : 's'}`;
  if (typeof v === 'number') return String(Math.round(v * 1000) / 1000);
  return String(v);
}
